import { EChartsOption } from 'echarts';
import { XAXisOption } from 'echarts/types/dist/shared';
import { XYChartDashoardItemFactoryOptions } from 'src/app/dashboards/common/items/xy-chart';
import { IQueryContext } from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextHelper } from 'src/app/services/query-context/query-context-helper';
import { NotImplementedError } from '../error';
import { datetimePartDateTrunc } from '../sql';

/**
 * Aggregation type for time intervals.
 *
 * @returns 'day' | 'week' | 'month' | 'quarter' | 'year'.
 */
export type dateAggregation = 'day' | 'week' | 'month' | 'quarter' | 'year';

const DAY_MS = 1000 * 60 * 60 * 24;

export interface CategoryLabelSortFn {
    (a: any, b: any): number;
}
/**
 * Tools for building and applying X-axis ticks and labels for ECharts items.
 *
 * @description
 * Provides:
 *  - Compute aggregation and custom tick positions from a date range.
 *  - Apply `axisTick.customValues` and `axisLabel.formatter` to an `EChartsOption` (`applyEchartsXAxisTicks`).
 *
 * @example
 * XYChartAxis.applyEchartsXAxisTicks(EChartsOption);
 */
export class XYChartAxis {
    /**
     * Build customValues for the X axis based on data rows.
     *
     * @param rows Array of objects with date strings.
     *
     * @returns Array of dates as string
     */
    private static buildAxisTicksLabelsCustomValues(rows: Date[]): string[] {
        const ticksAmount = 6;

        const { totalDays, minDate, secondDate, maxDate } =
            this.getRangeStats(rows);
        const aggregation: dateAggregation = this.getAggregation(
            minDate,
            secondDate
        );

        const formatDate = (d: Date) => d.toISOString().slice(0, 10); // YYYY-MM-DD
        const monthKey = (d: Date) =>
            `${d.getUTCFullYear()}-${d.getUTCMonth()}`;
        const nextTick = (d: Date) => this.moveTick(d, { aggregation });

        const monthlyLabel =
            totalDays > 140 &&
            (aggregation === 'day' || aggregation === 'week');

        // Skip the first month if it is not complete
        const stepMs = Math.max(
            1,
            nextTick(minDate).getTime() - minDate.getTime()
        );
        const skipFirstMonth =
            monthlyLabel &&
            monthKey(new Date(minDate.getTime() - stepMs)) ===
                monthKey(minDate);

        const ticks: string[] = [];
        const ignoredMonths = new Set<string>();

        for (let d = minDate; d <= maxDate; ) {
            if (!monthlyLabel) {
                ticks.push(formatDate(d));
            }

            // Special insertion of the X-axis indicator for weekly aggregation
            else {
                const key = monthKey(d);
                if (!ignoredMonths.has(key)) {
                    if (!(skipFirstMonth && key === monthKey(minDate))) {
                        ticks.push(formatDate(d));
                    }
                    ignoredMonths.add(key);
                }
            }

            const next = nextTick(d);

            if (next.getTime() <= d.getTime()) {
                throw new Error(
                    `moveTick() must advance date. 

Current tick = ${d.toISOString()}
Next tick = ${next.toISOString()}
`
                );
            }

            d = next;
        }

        if (ticks.length <= ticksAmount) return ticks;

        const keepEvery = Math.ceil((ticks.length - 1) / (ticksAmount - 1));
        return ticks
            .filter((_, i) => i % keepEvery === 0)
            .slice(0, ticksAmount);
    }

    /**
     * Moves the date tick one step based on the specified aggregation.
     *
     * @param current The current tick anchor date.
     * @param params.aggregation The aggregation step: 'day' | 'week' | 'month' | 'quarter' | 'year'.
     * @param params.totalDays The total range in days.
     *
     * @returns The next tick date aligned to the start of the target period.
     */
    private static moveTick(
        current: Date,
        params: {
            aggregation: dateAggregation;
        }
    ): Date {
        const { aggregation } = params;
        const next = new Date(current);

        switch (aggregation) {
            case 'day':
                next.setUTCDate(next.getUTCDate() + 1);
                break;
            case 'week':
                next.setUTCDate(next.getUTCDate() + 7);
                break;
            case 'month':
                next.setUTCMonth(next.getUTCMonth() + 1, 1);
                break;
            case 'quarter': {
                const currentMonth = next.getUTCMonth();
                const nextQuarterStartMonth =
                    Math.floor(currentMonth / 3) * 3 + 3;
                next.setUTCMonth(nextQuarterStartMonth, 1);
                break;
            }
            case 'year':
                next.setUTCFullYear(next.getUTCFullYear() + 1, 0, 1);
                break;
        }

        return next;
    }

    /**
     * Get aggregation type within first two dates.
     *
     * @param firstDate The first date of the data point
     * @param secondDate The second date of the data point, if missing, treated as firstDate
     *
     * @returns 'day' | 'week' | 'month' | 'quarter' | 'year'
     */
    private static getAggregation(
        firstDate: Date,
        secondDate: Date
    ): dateAggregation {
        const datesGap = Math.floor(
            (secondDate.getTime() - firstDate.getTime()) / DAY_MS
        );
        if (datesGap >= 365) return 'year';
        if (datesGap >= 90) return 'quarter';
        if (datesGap >= 30) return 'month';
        if (datesGap >= 7) return 'week';
        return 'day';
    }

    /**
     * Get range informations (like total days, first, second and last date) within certain data range present in rows.
     *
     * @param dates Array of objects with date strings.
     *
     * @returns { totalDays, minDate, secondDate, maxDate }
     */
    private static getRangeStats(dates: Date[]): {
        totalDays: number;
        minDate: Date;
        secondDate: Date;
        maxDate: Date;
    } {
        if (dates.length === 0) {
            const date = new Date();
            return {
                totalDays: 0,
                minDate: date,
                secondDate: date,
                maxDate: date,
            };
        }

        let minDate = dates[0];
        let maxDate = dates[0];
        for (let i = 1; i < dates.length; i++) {
            const ts = dates[i];
            if (ts < minDate) minDate = ts;
            if (ts > maxDate) maxDate = ts;
        }

        let secondDate = maxDate;
        for (const ts of dates) {
            if (ts > minDate && ts < secondDate) {
                secondDate = ts;
            }
        }

        const totalDays =
            Math.floor((maxDate.getTime() - minDate.getTime()) / DAY_MS) + 1;

        return {
            totalDays,
            minDate,
            secondDate,
            maxDate,
        };
    }

    /**
     * Get source from dataset
     *
     * @param echartsOption - ECharts object that contains options
     * @param datasetId - [optional] Dataset ID
     *
     * @returns Array of source rows.
     */
    private static getDatasetRows(
        echartsOption: EChartsOption,
        datasetId: string = 'dataset_raw'
    ): any[] {
        const datasets = (echartsOption.dataset as any[]) ?? [];
        const ds = datasets.find((d) => d?.id === datasetId);
        return (ds?.source as any[]) ?? [];
    }

    /** Replaces inplace the specified column in dataset rows with datetime values.
     *
     * @param echartsOption - ECharts object that contains options
     * @param columnKey - Key of the column to convert
     */
    public static datasetRowsToDatetime(
        echartsOption: EChartsOption,
        columnKey: string
    ) {
        const rows = this.getDatasetRows(echartsOption);
        rows.forEach((r) => {
            // if it's a number (unix timestamp), convert to milliseconds
            if (!isNaN(Number(r[columnKey!]))) {
                r[columnKey!] = new Date(Number(r[columnKey!]) * 1000);
            } else if (typeof r[columnKey!] === 'string') {
                r[columnKey!] = Date.parse(r[columnKey!]);
            } else {
                throw new Error(
                    'Unsupported datetimeKey format for filling missing values.'
                );
            }
        });
    }

    /**
     * Apply customValues to time chart xAxis
     *
     * @param echartsOption - ECharts object that contains options
     */
    public static applyEchartsTimeXAxisTicks(
        echartsOption: EChartsOption,
        dateKey: string,
        datetimeGranularity: datetimePartDateTrunc
    ): void {
        // const labelTotal = 'xAxis: total-applyTicks';
        // console.time(labelTotal);
        try {
            const dates: Date[] = this.getDatasetRows(echartsOption).map(
                (r) => new Date(r[dateKey])
            );

            const customValues = this.buildAxisTicksLabelsCustomValues(dates);
            if (!customValues.length) return;

            const { totalDays, minDate, secondDate } =
                this.getRangeStats(dates);
            const aggregation = this.getAggregation(minDate, secondDate);

            const prevAxis: any = echartsOption.xAxis ?? {};
            if (
                prevAxis.axisTick !== undefined &&
                prevAxis.axisTick.customValues !== undefined
            ) {
                throw new Error('Cannot override existing xAxis.axisTick.');
            }
            if (
                prevAxis.axisLabel !== undefined &&
                prevAxis.axisLabel.customValues !== undefined
            ) {
                throw new Error(
                    'Cannot override existing xAxis.axisLabel.customValues.'
                );
            }
            if (
                prevAxis.axisLabel !== undefined &&
                prevAxis.axisLabel.formatter !== undefined
            ) {
                throw new Error(
                    'Cannot override existing xAxis.axisLabel.formatter.'
                );
            }
            echartsOption.xAxis = {
                ...prevAxis,
                axisTick: {
                    ...(prevAxis.axisTick ?? {}),
                    customValues: [...customValues],
                },
                axisLabel: {
                    ...(prevAxis.axisLabel ?? {}),
                    customValues: [...customValues],
                    formatter: (value: any) => {
                        const date = new Date(value);
                        if (totalDays === 1) return '{yyyy}';
                        if (totalDays > 140 && aggregation === 'week')
                            return '{yyyy}-{MM}-{dd}';
                        if (totalDays > 140 || aggregation === 'month')
                            return '{yyyy}-{MM}';
                        return '{MM}-{dd}';
                    },
                },
            };
        } finally {
            // console.timeEnd(labelTotal);
        }
    }

    /**
     * Apply customValues to category chart xAxis
     *
     * @param echartsOption - ECharts object that contains options
     */
    public static applyEchartsCategoryXAxis(
        echartsOption: EChartsOption,
        categorySubtype: XYChartDashoardItemFactoryOptions['xAxisCategorySubtype'],
        dataKey: string,
        sortKey?: string,
        sortFn?: CategoryLabelSortFn,
        /** If provided, fill missing labels */
        fillMissingValues?: {
            enabled: true | false;
            datetimeGranularity?: datetimePartDateTrunc;
            axisDataFilter?: (l: any) => boolean;
        }
    ): void {
        try {
            const rows = this.getDatasetRows(echartsOption);

            let data: string[] | number[] = Array.from(
                rows.map((r) => r[dataKey])
            )
                // deduplicate
                .filter((item, index, self) => {
                    return index === self.findIndex((t) => t === item);
                });

            // sort axis data
            switch (categorySubtype) {
                case 'datetime':
                    // throw error if sortKey or sortFn is provided
                    if (sortKey || sortFn) {
                        throw new Error(
                            'Cannot provide sortKey or sortFn when xAxisCategorySubtype is datetime.'
                        );
                    }
                    // check if data are ISO date strings
                    data.forEach((d) => {
                        if (typeof d === 'string' && isNaN(Date.parse(d))) {
                            throw new Error(
                                `Invalid date string in x axis data: ${d}`
                            );
                        }
                    });
                    // sort datetime automatically
                    // if xAxisCategorySubtype is datetime, we assume labelVal is string returned as toISOString()
                    data.sort(
                        (a, b) => new Date(a).getTime() - new Date(b).getTime()
                    );
                    break;
                case 'generic':
                    // Reject numeric axis values. This function sets an explicit `xAxis.data`
                    // (see below), and in that mode ECharts reads a numeric series value as the
                    // category index instead of matching it against the category list possibly creating huge gaps
                    // and only looks correct while the values equal their own position (0, 1, 2, ... with no gaps).
                    const numericValue = (data as unknown[]).find(
                        (d) => typeof d === 'number'
                    );
                    if (numericValue !== undefined) {
                        throw new Error(
                            `Numeric values are not supported on a category x axis (column '${dataKey}', e.g. ${numericValue}). ` +
                                `Use xAxisType: 'value' for numeric data, or CAST the column to STRING in SQL.`
                        );
                    }
                    // error if both sortKey and sortFn are provided
                    if (sortKey && sortFn) {
                        throw new Error(
                            'Cannot provide both sortKey and sortFn. Please provide only one.'
                        );
                    }
                    if (!sortFn) {
                        if (sortKey) {
                            throw new NotImplementedError();
                        }
                    } else {
                        data.sort(sortFn);
                    }
                    break;
                default:
                    throw new Error(
                        `Unsupported xAxisCategorySubtype: ${categorySubtype}`
                    );
            }
            // data = data.map((l) => l.labelVal);

            if (data.length === 0) return;

            // Fill missing labels
            // if 'datetime'
            if (categorySubtype === 'datetime') {
                // require fillMissingValues.datetimeGranularity, throw error otherwise
                if (!fillMissingValues!.enabled) {
                    throw new Error(
                        'fillMissingValues.enabled must not be false when xAxisCategorySubtype is datetime'
                    );
                }
                if (!fillMissingValues!.datetimeGranularity) {
                    throw new Error(
                        'fillMissingValues.datetimeGranularity is required when xAxisCategorySubtype is datetime'
                    );
                }
            }
            if (fillMissingValues) {
                if (categorySubtype === 'datetime') {
                    // datetimeGranularity is required
                    if (!fillMissingValues.datetimeGranularity) {
                        throw new Error(
                            'datetimeGranularity is required when xAxisCategorySubtype is datetime'
                        );
                    }
                    const nextDatetime = (current: Date) => {
                        switch (fillMissingValues.datetimeGranularity) {
                            case 'hour':
                                current.setUTCHours(current.getUTCHours() + 1);
                                break;
                            case 'day':
                                current.setUTCDate(current.getUTCDate() + 1);
                                break;
                            case 'week':
                                current.setUTCDate(current.getUTCDate() + 7);
                                break;
                            case 'month':
                                current.setUTCMonth(current.getUTCMonth() + 1);
                                break;
                            case 'quarter':
                                current.setUTCMonth(current.getUTCMonth() + 3);
                                break;
                            case 'year':
                                current.setUTCFullYear(
                                    current.getUTCFullYear() + 1
                                );
                                break;
                            case 'total':
                                // no increment
                                break;
                        }
                        return current;
                    };
                    // get all existing dates
                    const timestamps: Date[] = Array.from(
                        new Set(data.map((d) => new Date(d)))
                    ).sort((a, b) => a.getTime() - b.getTime());
                    const minTimestamp = timestamps[0];
                    const maxTimestamp = timestamps[timestamps.length - 1];
                    // build all expected dates between min and max
                    let expectedData: Date[] = [];
                    let currentDate = new Date(minTimestamp);
                    while (currentDate <= maxTimestamp) {
                        expectedData.push(new Date(currentDate.getTime()));
                        // move to next date based on granularity
                        currentDate = nextDatetime(currentDate);
                    }

                    // filter expectedData based on axisDataFilter if provided
                    if (fillMissingValues.axisDataFilter) {
                        const axisFilter = fillMissingValues.axisDataFilter;
                        expectedData = expectedData.filter(axisFilter);
                    }

                    data = expectedData.map((d) => d.toISOString());
                } else {
                    throw new Error(
                        `Unsupported xAxisCategorySubtype for filling missing values: ${categorySubtype}`
                    );
                }
            }

            const prevAxis: any = echartsOption.xAxis ?? {};
            if (prevAxis.data !== undefined) {
                throw new Error('Cannot override existing xAxis.data.');
            }
            echartsOption.xAxis = {
                ...prevAxis,
                data: [...data],
            };

            // TODO setup ticks and labels the same way as time axis
            if (categorySubtype === 'datetime') {
                // calculate datetime labels and ticks
                (echartsOption.xAxis! as XAXisOption).axisLabel = {
                    ...((echartsOption.xAxis! as XAXisOption).axisLabel ?? {}),
                    formatter: (value: any) => {
                        const date = new Date(value);
                        const label = this.dateToLabelString(
                            date,
                            fillMissingValues!.datetimeGranularity!
                        );
                        return label;
                    },
                };
            }
        } finally {
            // console.timeEnd(labelTotal);
        }
    }

    public static dateToLabelString(
        date: Date,
        aggregation: datetimePartDateTrunc
    ): string {
        switch (aggregation) {
            case 'second':
                return date.toISOString().slice(0, 19); // YYYY-MM-DDTHH:MM:SS
            case 'minute':
                return date.toISOString().slice(0, 16); // YYYY-MM-DDTHH:MM
            case 'hour':
                return date.toISOString().slice(0, 13); // YYYY-MM-DDTHH
            case 'day':
                return date.toISOString().slice(0, 10); // YYYY-MM-DD
            case 'week':
                return date.toISOString().slice(0, 10); // YYYY-MM-DD
            case 'month':
            case 'quarter':
                return date.toISOString().slice(0, 7); // YYYY-MM
            case 'year':
                return date.getUTCFullYear().toString(); // YYYY
            case 'total':
                return 'Total';
            default:
                return date.toISOString().slice(0, 13); // YYYY-MM-DDTHH
        }
    }

    /**
     * Parse date column from row based on datetimeKey format.
     * @param row Data row object.
     * @param datetimeKey Key of the datetime column in the row.
     * @param aggregation Aggregation type for date truncation.
     * @returns Parsed date as a timestamp in milliseconds.
     */
    public static parseDateColumn(
        row: any,
        datetimeKey: string,
        aggregation: datetimePartDateTrunc
    ): number {
        // deduce fillMissingValues.datetimeKey format
        // if it's a number (unix timestamp), convert to milliseconds
        if (!isNaN(Number(row[datetimeKey!]))) {
            return Number(row[datetimeKey!]) * 1000;
        } else if (typeof row[datetimeKey!] === 'string') {
            return this.parseDateString(row[datetimeKey!], aggregation!);
        }
        throw new Error(
            'Unsupported datetimeKey format for filling missing values.'
        );
    }

    public static parseDateString(
        dateStr: string,
        aggregation: datetimePartDateTrunc
    ): number {
        switch (aggregation) {
            case 'second':
                // YYYY-MM-DDTHH:MM:SS
                return new Date(dateStr).getTime();
            case 'minute':
                // YYYY-MM-DDTHH:MM
                return new Date(dateStr + ':00Z').getTime();
            case 'hour':
                // YYYY-MM-DDTHH
                return new Date(dateStr + ':00:00Z').getTime();
            case 'day':
            case 'week':
                // YYYY-MM-DD
                return Date.parse(dateStr);
            case 'month':
            case 'quarter':
                // YYYY-MM
                return Date.parse(dateStr);
            case 'year':
                // YYYY
                return Date.parse(dateStr);
            case 'total':
                return 0;
            default:
                return new Date(dateStr).getTime();
        }
    }

    private static getWeekNumber(date: Date): number {
        // Create a copy of the date object
        const tempDate = new Date(
            Date.UTC(
                date.getUTCFullYear(),
                date.getUTCMonth(),
                date.getUTCDate()
            )
        );
        // Set to the nearest Thursday: current date + 4 - current day number
        // Make Sunday (0) be the last day of the week
        const dayNum = tempDate.getUTCDay() || 7;
        tempDate.setUTCDate(tempDate.getUTCDate() + 4 - dayNum);
        // Get first day of year
        const yearStart = new Date(Date.UTC(tempDate.getUTCFullYear(), 0, 1));
        // Calculate full weeks to nearest Thursday
        return Math.ceil(
            ((tempDate.getTime() - yearStart.getTime()) / 86400000 + 1) / 7
        );
    }

    /** Move date by one step based on aggregation type.
     * Used for filling missing values.
     *
     * @param current The current date.
     * @param aggregation The aggregation type for date truncation.
     * @param steps The number of steps to move the date.
     * @returns The new date after moving by the specified steps.
     */
    private static moveDateByGranularity(
        current: Date,
        aggregation: datetimePartDateTrunc,
        steps: number = 1
    ): Date {
        const next = new Date(current);
        const normalizedAggregation =
            aggregation.toLowerCase() as datetimePartDateTrunc;

        switch (normalizedAggregation) {
            case 'second':
                next.setUTCSeconds(next.getUTCSeconds() + steps);
                break;
            case 'minute':
                next.setUTCMinutes(next.getUTCMinutes() + steps);
                break;
            case 'hour':
                next.setUTCHours(next.getUTCHours() + steps);
                break;
            case 'day':
                next.setUTCDate(next.getUTCDate() + steps);
                break;
            case 'week':
                next.setUTCDate(next.getUTCDate() + 7 * steps);
                break;
            case 'month':
                next.setUTCMonth(next.getUTCMonth() + steps);
                break;
            case 'quarter':
                next.setUTCMonth(next.getUTCMonth() + 3 * steps);
                break;
            case 'year':
                next.setUTCFullYear(next.getUTCFullYear() + steps);
                break;
            case 'total':
                break;
            default:
                throw new Error(
                    `Unsupported datetime granularity: ${aggregation}`
                );
        }

        return next;
    }

    /**
     * Checks if a row is a gap break row (all metric values are null).
     * Used to prevent inserting a duplicate gap break before rows that are
     * already all-null.
     * @param row The data row to check.
     * @param datetimeKey The key of the datetime column.
     * @param dimensionKeys The keys of the dimension columns.
     * @returns True if the row is a gap break row, false otherwise.
     */
    private static isGapBreakRow(
        row: any,
        datetimeKey: string,
        dimensionKeys: string[] = []
    ): boolean {
        const skipKeys = new Set([datetimeKey, ...dimensionKeys]);
        return Object.keys(row)
            .filter((key) => !skipKeys.has(key))
            .every((key) => row[key] === null);
    }

    /**
     * Inserts a null-valued row between consecutive data points that are farther
     * apart than one `dateAggregation` step. ECharts treats null metric
     * values as gaps, so the line breaks instead of connecting across the hole.
     * @param rows The array of data rows to modify in-place.
     * @param queryContext The query context, used to determine split dimension keys.
     * @param datetimeKey The key of the datetime column in the rows.
     * @param dateAggregation The aggregation type for determining the expected interval between data points.
     * @param options Optional parameters:
     *  - dateFormatter: A function to format unique date, e.g. 'YYYY-MM'.
     */
    public static fillMissingTimeGapRows(
        rows: any[],
        queryContext: IQueryContext,
        datetimeKey: string,
        dateAggregation: datetimePartDateTrunc,
        options?: {
            dateFormatter?: (date: Date) => string;
            /** Override grouping keys when rows use a compiled dimension such as `bucket_group`. */
            dimensionKeys?: string[];
        }
    ): void {
        const dimensionKeys =
            options?.dimensionKeys ??
            QueryContextHelper.getSplitDimensionKeys(queryContext);

        if (rows.length < 2 || dateAggregation === 'total') return;

        const rowsWithBreaks: any[] = [];

        // Track the previous timestamp and whether it was a gap break for each group of dimensions
        const prevTimestampByGroup = new Map<string, number>();
        const prevWasGapBreakByGroup = new Map<string, boolean>();

        // Iterate through rows and insert gap break rows where needed
        for (const row of rows) {
            const currentTimestamp = new Date(row[datetimeKey]).getTime();
            const currentRowIsGapBreak = this.isGapBreakRow(
                row,
                datetimeKey,
                dimensionKeys
            );
            const groupKey = dimensionKeys.map((k) => row[k]).join('\0');
            const previousTimestamp =
                prevTimestampByGroup.get(groupKey) ?? null;
            const previousRowWasGapBreak =
                prevWasGapBreakByGroup.get(groupKey) ?? false;

            // Insert a gap break row if the current timestamp is more than one step away from the previous timestamp
            if (
                previousTimestamp !== null &&
                !previousRowWasGapBreak &&
                !isNaN(currentTimestamp) &&
                currentTimestamp !== previousTimestamp
            ) {
                const expectedTimestamp = this.moveDateByGranularity(
                    new Date(previousTimestamp),
                    dateAggregation
                ).getTime();
                if (expectedTimestamp < currentTimestamp) {
                    const gapBreakRow = QueryContextHelper.cleanRow(
                        row,
                        datetimeKey,
                        dimensionKeys
                    );
                    // Set date one step before current row, preserving source format
                    const gapDate = this.moveDateByGranularity(
                        new Date(currentTimestamp),
                        dateAggregation,
                        -1
                    );
                    const originalDateStr = String(row[datetimeKey]);
                    if (options?.dateFormatter) {
                        gapBreakRow[datetimeKey] =
                            options.dateFormatter(gapDate);
                    } else if (/^\d{4}-\d{2}-\d{2}T/.test(originalDateStr)) {
                        gapBreakRow[datetimeKey] = gapDate.toISOString();
                    } else if (/^\d{4}-\d{2}-\d{2}$/.test(originalDateStr)) {
                        gapBreakRow[datetimeKey] = gapDate
                            .toISOString()
                            .split('T')[0];
                    } else {
                        throw new Error(
                            `fillMissingTimeGapRows: unrecognized date format "${originalDateStr}". Provide a dateFormatter.`
                        );
                    }
                    rowsWithBreaks.push(gapBreakRow);
                }
            }

            rowsWithBreaks.push(row);

            // Update previous timestamp and gap break status for the current group
            if (!isNaN(currentTimestamp)) {
                prevTimestampByGroup.set(groupKey, currentTimestamp);
                prevWasGapBreakByGroup.set(groupKey, currentRowIsGapBreak);
            } else {
                prevWasGapBreakByGroup.set(groupKey, false);
            }
        }

        if (rowsWithBreaks.length === rows.length) return;
        rows.length = 0;
        rows.push(...rowsWithBreaks);
    }
}
